<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import ReportHowWeCount from 'dashboard/components-next/reports/ReportHowWeCount.vue';
import ReportLiveCountBanner from 'dashboard/components-next/reports/ReportLiveCountBanner.vue';
import QuestionReportBlocks from 'dashboard/components-next/csat/QuestionReportBlocks.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import { generateFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    CsatTable,
    QuestionReportBlocks,
    ReportHowWeCount,
    ReportLiveCountBanner,
    ReportFilterSelector,
    PageLayout,
    PageHeader,
    V4Button,
  },
  data() {
    return {
      pageIndex: 0,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      ratingScale: null,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
      questionBlocks: 'csat/getQuestions',
      csatMetrics: 'csat/getMetrics',
    }),
    // Pesquisas enviadas contadas na hora (Reports::HourlyCsatSent), com o motivo.
    sentCoverage() {
      return this.csatMetrics.sentCoverage;
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
        rating_scale: this.ratingScale,
      };
    },
    // Só o que o topo e a tabela NÃO mostram: perguntas de nota/escolha
    // personalizadas. A pesquisa padrão (nota 1 a 5 + comentário) já está no
    // topo, e texto livre já está na coluna de comentário — repetir em blocos
    // poluía o relatório.
    customQuestions() {
      return this.questionBlocks.filter(
        block =>
          block.type !== 'text' &&
          !(
            block.key === 'rating' &&
            block.scale_min === 1 &&
            block.scale_max === 5
          )
      );
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
  },
  methods: {
    getAllData() {
      try {
        this.$store.dispatch('csat/getMetrics', this.requestPayload);
        // Blocos por pergunta (pesquisa respondida no chat). Sem pesquisa
        // customizada a lista volta vazia e a seção nem aparece.
        this.$store.dispatch('csat/getQuestionMetrics', this.requestPayload);
        this.getResponses();
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        ...this.requestPayload,
      });
    },
    downloadReports() {
      const type = 'csat';
      try {
        this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type, to: this.to }),
          ...this.requestPayload,
        });
      } catch (error) {
        useAlert(this.$t('REPORT.CSAT_REPORTS.DOWNLOAD_FAILED'));
      }
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.getResponses();
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
    }) {
      // do not track filter change on inital load
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: 'csat',
        });
      }

      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;
      this.ratingScale = selectedRating
        ? `${selectedRating.scaleMin}-${selectedRating.scaleMax}`
        : null;

      this.getAllData();
    },
  },
};
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-smile"
        accent="iris"
        :title="$t('CSAT_REPORTS.HEADER')"
        :subtitle="$t('CSAT_REPORTS.SUBTITLE')"
      >
        <template #actions>
          <V4Button
            :label="$t('CSAT_REPORTS.DOWNLOAD')"
            icon="i-ph-download-simple"
            size="sm"
            @click="downloadReports"
          />
        </template>
      </PageHeader>
    </template>

    <div class="flex flex-col gap-4">
      <!-- include-non-human-agents: agentes de IA são usuários avaliáveis
           pelo CSAT — sem isto o filtro nunca listava as cascas "IA: ..." -->
      <ReportFilterSelector
        show-agents-filter
        include-non-human-agents
        show-inbox-filter
        show-rating-filter
        :show-team-filter="isTeamsEnabled"
        :show-business-hours-switch="false"
        @filter-change="onFilterChange"
      />

      <!-- time e responsável da pesquisa enviada são retrato (Reports::HourlyCsatSent). -->
      <ReportHowWeCount />
      <ReportLiveCountBanner :coverage="sentCoverage" />
      <CsatMetrics :filters="requestPayload" />
      <QuestionReportBlocks
        v-if="customQuestions.length"
        :questions="customQuestions"
      />
      <CsatTable :page-index="pageIndex" @page-change="onPageNumberChange" />
    </div>
  </PageLayout>
</template>
